<template>
  <div>
    <!-- Confirm delete room -->
    <hl-Modal
      v-if="showDeleteRoomModal"
      class="text-center"
      iconClass="budicon-trash text-danger"
      :title="'Er du sikker på at du vil slette «' + deletingRoom + '»'"
    >
      <div>
        <button class="btn btn-danger mx-2" @click="deleteRoom(deletingRoomIdx)">Bekreft</button>
        <button class="btn btn-outline-secondary mx-2" @click="showDeleteRoomModal = false">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- Confirm delete room -->

    <!-- Error saving rooms -->
    <hl-Modal
      v-if="showErrorModal"
      class="text-center"
      iconClass="budicon-cloud-server text-warning"
      title="Oops! ️️🕵️‍♂️ Noe gikk galt. Vennligst prøv på nytt"
    >
      <hl-Loader v-if="!loaded" variation="warning" />

      <div v-else>
        <button class="btn btn-warning mx-2" @click="patchRentalObject">Prøv igjen</button>
        <button class="btn btn-outline-secondary mx-2" @click="showErrorModal = false">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- Error saving rooms -->

    <BlockContainer class="p-5">
      <ValidationObserver v-slot="{ invalid, dirty }">
        <div class="row align-items-center">
          <div class="col">
            <h5>Leieobjektets rom og arealer</h5>
          </div>

          <div class="col edit-buttons text-right">
            <div v-if="editMode">
              <CancelButton class="mr-1" @click.native="resetRoomsAndCloseEditMode" />
              <SaveButton
                :loading="loading"
                @click.native="patchRentalObject"
                class="ml-1"
                :disabled="invalid || loading"
              />
            </div>

            <EditButton v-else @click.native="setEditModeAndLocalCopy" />
          </div>
        </div>

        <!-- Loading Mask Room -->
        <div v-if="!loaded" class="row mt-5">
          <div v-for="(count, i) in 10" :key="i" class="col room-fixed-width">
            <div class="flip-card room">
              <div class="flip-card-inner">
                <div class="flip-card-front">
                  <hl-Mask :height="30" :width="30"></hl-Mask>
                  <hl-Mask :height="18" :width="100" class="ml-2"></hl-Mask>
                  <hl-Mask :height="10" :width="70" class="mt-3"></hl-Mask>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- / Loading Mask Room -->

        <div v-else class="roomsContainer mt-5">
          <div v-for="(room, index) in rooms" :key="index" class="roomsContainer__roomsEntry">
            <ValidationProvider
              :name="`Antall ${getRoomName(room.name).plural || room.name}`"
              :rules="validationRules.rentalObject.rentalObject.roomCount"
              v-slot="{ errors }"
            >
              <hl-Input
                type="number"
                :name="'details.room_' + index + '_roomCount'"
                size="sm"
                placeholder="rom"
                v-model="room.count"
                nameForValidation="Antall"
                :disabled="!editMode"
                :error="errors[0]"
              />
            </ValidationProvider>

            <ValidationProvider
              v-if="!room.default && editMode"
              name="Rom navn"
              :rules="validationRules.rentalObject.rentalObject.roomName"
              v-slot="{ errors }"
            >
              <hl-Input
                type="text"
                :name="'details.room_' + index + '_roomName'"
                size="sm"
                placeholder="rom"
                :value="room.name"
                v-model="room.name"
                :error="errors[0]"
              />
            </ValidationProvider>

            <span v-else>
              {{ room.count >= 2 ? getRoomName(room.name).plural : getRoomName(room.name).singular }}
            </span>

            <div class="d-flex align-items-center">
              <input type="checkbox" v-model="room.shared" :disabled="!editMode" />
              <span class="ml-2">Fellesrom</span>
            </div>

            <div
              v-if="!room.default && editMode"
              class="roomsContainer__roomsEntry__delete"
              @click="deleteRoomModal(room.name, index)"
            >
              <i class="budicon-solid budicon-solid-trash" />
            </div>
          </div>
          <div v-if="editMode" class="roomsContainer__addNewRoom" @click="addNewRoom">
            <span>
              <i class="h3 budicon-plus-ui" />
            </span>
            <span>Legg til ett nytt rom</span>
          </div>
        </div>
      </ValidationObserver>
    </BlockContainer>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
//import hlValidation from "@modules/husleie-framework/src/mixins/hlValidation";
import { mapGetters } from 'vuex';
import addValidationRules from '@/mixins/addValidationRules.js';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import Checkbox from '@modules/husleie-framework/src/components/forms/inputs/checkbox/Checkbox.vue';
import Mask from '@modules/husleie-framework/src/components/loaders/Mask.vue';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    'hl-Modal': Modal,
    'hl-Input': Input,
    'hl-Loader': Loader,
    'hl-Checkbox': Checkbox,
    'hl-Mask': Mask,
  },

  //mixins: [hlValidation],

  data() {
    return {
      editMode: false,
      rooms: [],
      showDeleteRoomModal: false,
      deletingRoom: '',
      deletingRoomIdx: null,
      localCopy: null,
      loading: false,
      showErrorModal: false,
    };
  },
  props: {
    rentalObjectId: [Number, String],
  },

  mixins: [addValidationRules],

  computed: {
    ...mapGetters(['getRentalObject', 'isExtendedRentalObjectLoaded']),

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },

    patchBody() {
      return {
        details: {
          rooms: this.rooms,
        },
      };
    },
  },

  methods: {
    validate() {
      this.$validator.validate('rooms.*');
    },

    setEditModeAndLocalCopy() {
      this.editMode = true;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.rentalObject)));
    },

    resetRoomsAndCloseEditMode() {
      this.resetRooms();
      this.editMode = false;
    },

    patchRentalObject() {
      this.loading = true;
      this.$store.dispatch('patchRentalObject', {
        rentalObjectId: this.rentalObjectId,
        body: this.patchBody,
        success: () => {
          this.editMode = false;
          this.$toast('Lagret', 'success');
        },
        error: () => {
          this.showErrorModal = true;
          this.$toast('Kunne ikke lagre', 'error');
        },
      });
      this.loading = false;
    },

    addNewRoom() {
      this.rooms.push({
        count: 0,
        name: 'navn',
        shared: false,
      });
    },

    deleteRoomModal(room, idx) {
      this.showDeleteRoomModal = true;
      this.deletingRoom = room;
      this.deletingRoomIdx = idx;
    },

    deleteRoom(idx) {
      this.rooms.splice(idx, 1);
      this.showDeleteRoomModal = false;
    },

    getRoomName(room) {
      if (room === 'kj_kken') {
        return {
          singular: 'Kjøkken',
          plural: 'Kjøkken',
        };
      }

      if (room === 'stue') {
        return {
          singular: 'Stue',
          plural: 'Stuer',
        };
      }

      if (room === 'bad') {
        return {
          singular: 'Bad',
          plural: 'Bad',
        };
      }

      if (room === 'gang') {
        return {
          singular: 'Gang',
          plural: 'Ganger',
        };
      }

      if (room === 'toalett') {
        return {
          singular: 'Toalett',
          plural: 'Toaletter',
        };
      }

      if (room === 'soverom') {
        return {
          singular: 'Soverom',
          plural: 'Soverom',
        };
      }

      if (room === 'alkove_hems') {
        return {
          singular: 'Hems',
          plural: 'Hemser',
        };
      }

      if (room === 'kjeller_bod') {
        return {
          singular: 'Kjeller / bod',
          plural: 'Kjellere / boder',
        };
      }

      if (room === 'hage') {
        return {
          singular: 'Hage',
          plural: 'Hager',
        };
      }

      if (room === 'balkong') {
        return {
          singular: 'Balkong',
          plural: 'Balkonger',
        };
      }

      if (room === 'garasje') {
        return {
          singular: 'Garasje',
          plural: 'Garasjer',
        };
      } else
        return {
          singular: room,
          plural: room,
        };
    },

    resetRooms() {
      if (this.rentalObject == null) return;
      const rooms = this.rentalObject.details.rooms;
      if (rooms == null) {
        return;
      }
      this.rooms = JSON.parse(JSON.stringify(rooms));
    },
  },
  watch: {
    rentalObject: {
      handler(value) {
        if (!value) {
          return;
        }
        this.resetRooms();
      },
    },
  },
};
</script>

<style scoped lang="scss">
.roomsContainer {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.roomsContainer__roomsEntry {
  display: grid;
  grid-template-columns: 45px 2fr 1fr;
  align-items: center;
  position: relative;
  padding: 1rem;
  gap: 0rem 1rem;
  background: $light;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
}

.roomsContainer__addNewRoom {
  display: flex;
  align-items: center;
  gap: 0rem 1.5rem;
  padding: 1rem;
  cursor: pointer;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
}

.roomsContainer__roomsEntry__delete {
  position: absolute;
  top: -14px;
  right: -14px;
  background: $secondary;
  color: white;
  padding-top: 4px;
  font-size: 0.9rem;
  width: 28px;
  height: 28px;
  text-align: center;
  border-radius: 50%;
  cursor: pointer;
}
</style>
